/**
 * Risks I Own on My Work. Count is active risks (not closed).
 * A failed load shows Unavailable and never 0. No owned risks is a plain empty state.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '@/state/workspace.store';
import { listRisksIOwn, type RisksIOwnResult } from '@/pages/my-work/risksIOwn.api';

export function RisksIOwnCard() {
  const navigate = useNavigate();
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace);
  const [result, setResult] = useState<RisksIOwnResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    listRisksIOwn()
      .then((next) => {
        if (!cancelled) setResult(next);
      })
      .catch(() => {
        if (!cancelled) setResult({ status: 'unavailable' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4" data-testid="risks-i-own-card">
      <h2 className="text-sm font-medium text-slate-500">Risks I Own</h2>
      {result === null ? (
        <p className="mt-2 text-sm text-slate-500" data-testid="risks-i-own-loading">
          Loading
        </p>
      ) : result.status === 'unavailable' ? (
        <p className="mt-2 text-sm text-slate-700" data-testid="risks-i-own-unavailable">
          Unavailable
        </p>
      ) : result.items.length === 0 ? (
        <p className="mt-2 text-sm text-slate-700" data-testid="risks-i-own-empty">
          You don&apos;t own any active risks.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100" data-testid="risks-i-own-list">
          {result.items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="flex w-full items-baseline justify-between gap-3 py-2 text-left text-sm text-slate-900 hover:text-slate-700"
                data-testid={`risks-i-own-link-${item.id}`}
                onClick={() => {
                  setActiveWorkspace(item.workspaceId);
                  navigate(`/projects/${item.projectId}/risks`);
                }}
              >
                <span>{item.title}</span>
                {item.projectName ? (
                  <span className="shrink-0 text-xs text-slate-500">{item.projectName}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
